import { useState, useEffect } from 'react';
import { Lock, Search, Trash2, CalendarClock, ShieldAlert, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { stockApi, busesApi, ventasApi } from '../lib/api';
import { topeVenta } from '../lib/autorizacionKit';
import { coincideProducto } from '../lib/buscar';
import { fechaLima } from '../lib/fechas';
import BusSelect from '../components/BusSelect';

const CLAVE_ADMIN = '2020';

function obtenerPrimeroDelAnio(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function hoyStr(): string {
  return fechaLima(new Date());
}

const VentaRetroactiva = () => {
  const [desbloqueado, setDesbloqueado] = useState(false);
  const [claveIngresada, setClaveIngresada] = useState('');

  const [productos, setProductos] = useState<any[]>([]);
  const [buses, setBuses] = useState<any[]>([]);
  const [carrito, setCarrito] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [cantidadEditando, setCantidadEditando] = useState<Record<string, string>>({});
  const [cantidadEnCmPorItem, setCantidadEnCmPorItem] = useState<Record<string, boolean>>({});

  const [fecha, setFecha] = useState(hoyStr());
  const [hora, setHora] = useState('12:00');
  const [padronBus, setPadronBus] = useState('');
  const [clienteNombre, setClienteNombre] = useState('Cliente Contado');
  const [tipoPago, setTipoPago] = useState<'efectivo' | 'tarjeta' | 'transferencia' | 'yape' | 'credito'>('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [descontarStock, setDescontarStock] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const fechaMinima = obtenerPrimeroDelAnio();

  useEffect(() => {
    if (!desbloqueado) return;
    stockApi.getFull().then((data) => setProductos((data || []).filter((p: any) => p.estado === 'activo' && p.tipo !== 'herramienta'))).catch(() => {});
    busesApi.getAll().then((data) => setBuses(data || [])).catch(() => {});
  }, [desbloqueado]);

  const validarClave = () => {
    if (claveIngresada === CLAVE_ADMIN) {
      setDesbloqueado(true);
    } else {
      toast.error('Clave incorrecta');
      setClaveIngresada('');
    }
  };

  // Igual que en VentasCompleto: el codigo tambien se compara normalizado por si el
  // lector de codigo de barras escribio apostrofe en vez de guion.
  const productosFiltrados = busqueda.trim()
    ? productos.filter(p => {
        return coincideProducto(busqueda, p);
      })
    : [];

  const agregarAlCarrito = (producto: any) => {
    setCarrito(prev => {
      const existente = prev.find(i => i.id === producto.id);
      if (existente) {
        return prev.map(i => i.id === producto.id ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      return [...prev, { id: producto.id, nombre: producto.nombre, precio: producto.precio_venta || 0, precio_compra: producto.precio_compra || 0, unidad: producto.unidad || 'unidad', cantidad: 1, stockDisponible: topeVenta(producto) }];
    });
    setBusqueda('');
  };

  const cambiarCantidad = (id: string, cantidad: number) => {
    setCarrito(prev => prev.map(i => i.id === id ? { ...i, cantidad: Math.max(0.01, cantidad) } : i));
    setCantidadEditando(prev => {
      const { [id]: _omitido, ...resto } = prev;
      return resto;
    });
  };

  // Ver la explicacion del mismo patron en VentasCompleto.tsx: sin este buffer, el
  // instante en que el input queda vacio mientras se tipea un decimal se interpreta
  // como una cantidad invalida.
  const confirmarCantidadTexto = (id: string) => {
    const texto = cantidadEditando[id];
    if (texto === undefined) return;
    const parsed = parseFloat(texto);
    if (!isNaN(parsed) && parsed > 0) {
      const factor = cantidadEnCmPorItem[id] ? 100 : 1;
      cambiarCantidad(id, parsed / factor);
    } else {
      setCantidadEditando(prev => {
        const { [id]: _omitido, ...resto } = prev;
        return resto;
      });
    }
  };

  const quitarDelCarrito = (id: string) => {
    setCarrito(prev => prev.filter(i => i.id !== id));
  };

  // Servicio manual: igual que en Ventas normales, un costo independiente sin
  // producto de stock detras (ver el mismo patron y su explicacion en
  // VentasCompleto.tsx). Opcional: solo aparece si se necesita.
  const [mostrarFormServicio, setMostrarFormServicio] = useState(false);
  const [servicioDescripcion, setServicioDescripcion] = useState('');
  const [servicioMonto, setServicioMonto] = useState('');

  const agregarServicio = () => {
    const monto = parseFloat(servicioMonto) || 0;
    if (!servicioDescripcion.trim()) {
      toast.error('Describe el servicio');
      return;
    }
    if (monto <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setCarrito(prev => [...prev, {
      id: `srv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      nombre: `Servicio: ${servicioDescripcion.trim()}`,
      precio: monto,
      precio_compra: 0,
      cantidad: 1,
      unidad: 'unidad',
      es_servicio: true
    }]);
    setServicioDescripcion('');
    setServicioMonto('');
    setMostrarFormServicio(false);
    toast.success('Servicio agregado al carrito');
  };

  // El precio del stock ya es el precio final: no se agrega IGV ni ningun otro cargo.
  const subtotal = carrito.reduce((sum, i) => sum + i.precio * i.cantidad, 0);
  const igv = 0;
  const total = subtotal;

  const limpiarFormulario = () => {
    setCarrito([]);
    setPadronBus('');
    setClienteNombre('Cliente Contado');
    setTipoPago('efectivo');
    setObservaciones('');
    setDescontarStock(false);
  };

  const registrarVenta = async () => {
    if (carrito.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }
    if (tipoPago === 'credito' && !padronBus) {
      toast.error('El padrón del bus es obligatorio para ventas a crédito o mixtas');
      return;
    }
    if (!fecha) {
      toast.error('Selecciona la fecha de la venta');
      return;
    }
    if (fecha < fechaMinima || fecha > hoyStr()) {
      toast.error(`La fecha debe estar entre el ${fechaMinima} y hoy`);
      return;
    }
    setEnviando(true);
    try {
      const venta = await ventasApi.crearRetroactiva({
        clave: CLAVE_ADMIN,
        fecha: new Date(`${fecha}T${hora}:00`).toISOString(),
        cliente_nombre: clienteNombre,
        placa_bus: padronBus || null,
        tipo_pago: tipoPago,
        items: carrito,
        subtotal,
        igv,
        total,
        observaciones,
        afecta_stock: descontarStock
      });
      toast.success(`Venta ${venta.numero} registrada con fecha ${fecha}${descontarStock ? '' : ' (sin descontar stock)'}. Quedó en Auditoría.`);
      limpiarFormulario();
    } catch (error: any) {
      toast.error('Error al registrar: ' + error.message);
    } finally {
      setEnviando(false);
    }
  };

  if (!desbloqueado) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-sm text-center">
          <Lock className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-1">Carga de ventas pasadas</h2>
          <p className="text-sm text-gray-500 mb-4">Acceso restringido. Ingresa la clave de administrador.</p>
          <input
            type="password"
            value={claveIngresada}
            onChange={(e) => setClaveIngresada(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && validarClave()}
            placeholder="Clave"
            className="w-full text-center text-lg tracking-widest p-2 border rounded-lg mb-3"
            autoFocus
          />
          <button
            onClick={validarClave}
            className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900"
          >
            Ingresar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock className="w-6 h-6 text-gray-700" />
        <h1 className="text-2xl font-bold">Carga de Venta Pasada</h1>
      </div>
      <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        No se imprime ticket. Cada carga queda registrada en Auditoría con tu usuario.
      </div>

      <div className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 mb-6 ${descontarStock ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
        <div>
          <p className="text-sm font-medium text-gray-800">Descontar del stock</p>
          <p className="text-xs text-gray-500">
            {descontarStock
              ? 'Se va a restar la cantidad de cada producto del inventario actual.'
              : 'Apagado: el inventario actual no se toca (recomendado para cargar historial pasado).'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={descontarStock}
          onClick={() => setDescontarStock(v => !v)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${descontarStock ? 'bg-red-600' : 'bg-gray-300'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${descontarStock ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Fecha de la venta * (desde {fechaMinima})</label>
          <input
            type="date"
            value={fecha}
            min={fechaMinima}
            max={hoyStr()}
            onChange={(e) => setFecha(e.target.value)}
            className="w-full p-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Hora aproximada</label>
          <input
            type="time"
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            className="w-full p-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bus / Padrón *</label>
          <BusSelect buses={buses} value={padronBus} onChange={(v) => setPadronBus(v)} className="w-full p-2 border rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de pago</label>
          <select
            value={tipoPago}
            onChange={(e) => setTipoPago(e.target.value as any)}
            className="w-full p-2 border rounded-lg"
          >
            <option value="efectivo">Efectivo</option>
            <option value="tarjeta">Tarjeta</option>
            <option value="transferencia">Transferencia</option>
            <option value="yape">Yape</option>
            <option value="credito">Crédito</option>
          </select>
        </div>

        {tipoPago === 'credito' && (
          <div className="md:col-span-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-sm text-purple-800">
            {padronBus ? (
              <>El crédito queda a nombre del bus <strong>{padronBus}</strong>. Deuda actual: S/ {(buses.find(b => b.padron === padronBus)?.credito_usado || 0).toFixed(2)}</>
            ) : (
              'Seleccioná el bus/padrón arriba: el crédito se registra a su nombre, no al de una persona.'
            )}
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Cliente</label>
          <input
            type="text"
            value={clienteNombre}
            onChange={(e) => setClienteNombre(e.target.value)}
            className="w-full p-2 border rounded-lg"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Observaciones</label>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={2}
            className="w-full p-2 border rounded-lg"
            placeholder="Motivo de la carga retroactiva, referencia, etc."
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="font-semibold mb-3">Agregar productos</h3>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar producto por nombre o código..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg"
          />
        </div>
        {busqueda.trim() && (
          <div className="max-h-56 overflow-y-auto space-y-2 mb-3">
            {productosFiltrados.slice(0, 20).map((p) => (
              <button
                key={p.id}
                onClick={() => agregarAlCarrito(p)}
                disabled={descontarStock && topeVenta(p) <= 0}
                className="w-full text-left border rounded-lg p-2 hover:bg-gray-50 disabled:opacity-40 flex justify-between items-center"
              >
                <div>
                  <p className="font-medium text-sm">{p.nombre}{p.es_kit && <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-semibold">KIT</span>}</p>
                  <p className="text-xs text-gray-500">{p.es_kit ? 'Kits armables' : 'Stock'}: {p.stock}</p>
                </div>
                <p className="font-bold text-green-600 text-sm">S/ {(p.precio_venta || 0).toFixed(2)}</p>
              </button>
            ))}
            {productosFiltrados.length === 0 && <p className="text-center text-gray-500 text-sm py-3">Sin resultados</p>}
          </div>
        )}

        {/* Servicio manual: opcional, costo independiente sin producto de stock */}
        <div className="mb-3 border border-orange-200 bg-orange-50 rounded-lg p-3">
          {!mostrarFormServicio ? (
            <button
              onClick={() => setMostrarFormServicio(true)}
              className="w-full flex items-center justify-center gap-2 text-orange-700 font-medium text-sm py-1"
            >
              <Wrench className="w-4 h-4" />
              + Agregar servicio (costo manual, sin stock)
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-orange-800 flex items-center gap-1">
                <Wrench className="w-4 h-4" /> Servicio manual
              </p>
              <input
                type="text"
                value={servicioDescripcion}
                onChange={(e) => setServicioDescripcion(e.target.value)}
                placeholder="Descripción del servicio"
                className="w-full p-2 border rounded text-sm"
              />
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={servicioMonto}
                onChange={(e) => setServicioMonto(e.target.value)}
                placeholder="Monto (S/)"
                className="w-full p-2 border rounded text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={agregarServicio}
                  className="flex-1 bg-orange-600 text-white py-1.5 rounded text-sm font-medium hover:bg-orange-700"
                >
                  Agregar al carrito
                </button>
                <button
                  onClick={() => { setMostrarFormServicio(false); setServicioDescripcion(''); setServicioMonto(''); }}
                  className="px-3 bg-gray-200 rounded text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {carrito.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">Carrito vacío</p>
        ) : (
          <div className="divide-y">
            {carrito.map((item) => (
              <div key={item.id} className="py-2 flex items-center justify-between gap-3">
                <span className="text-sm flex-1">
                  {item.es_servicio && <Wrench className="w-3 h-3 inline mr-1 text-orange-600" />}
                  {item.nombre}
                </span>
                {item.es_servicio && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Servicio</span>
                )}
                {!item.es_servicio && item.unidad === 'metro' && (
                  <button
                    type="button"
                    onClick={() => {
                      setCantidadEnCmPorItem(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                      setCantidadEditando(prev => { const { [item.id]: _o, ...resto } = prev; return resto; });
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {cantidadEnCmPorItem[item.id] ? 'cm' : 'm'}
                  </button>
                )}
                {!item.es_servicio && (
                <input
                  type="number"
                  step="0.01"
                  min={0.01}
                  max={descontarStock ? (item.unidad === 'metro' && cantidadEnCmPorItem[item.id] ? item.stockDisponible * 100 : item.stockDisponible) : undefined}
                  value={cantidadEditando[item.id] ?? (cantidadEnCmPorItem[item.id] ? item.cantidad * 100 : item.cantidad)}
                  onChange={(e) => setCantidadEditando(prev => ({ ...prev, [item.id]: e.target.value }))}
                  onBlur={() => confirmarCantidadTexto(item.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  className="w-16 p-1 border rounded text-center text-sm"
                />
                )}
                <span className="text-sm font-semibold w-24 text-right">S/ {(item.precio * item.cantidad).toFixed(2)}</span>
                <button onClick={() => quitarDelCarrito(item.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border-t mt-4 pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>S/ {subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold text-lg"><span>Total</span><span>S/ {total.toFixed(2)}</span></div>
        </div>
      </div>

      <button
        onClick={registrarVenta}
        disabled={enviando}
        className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
      >
        {enviando ? 'Registrando...' : 'Registrar venta pasada (sin ticket)'}
      </button>
    </div>
  );
};

export default VentaRetroactiva;
